import {
    BaseKeyProvider,
    createKeyMaterialFromBuffer,
    Room,
    ConnectionState,
    LocalVideoTrack,
    RpcError,
    RoomEvent,
    Track,
    VideoQuality
} from "livekit-client";
import { ClientEvent, ClientPrefix, MatrixEventEvent, Method } from "matrix-js-sdk";
import { getCallParticipantLabel } from "src/scenes/Chat/components/call/ChatCallPanel.constants";
import {
    MEETING_EVENT_METADATA_FIELD,
    SHARED_CALL_SUMMARY_FIELD
} from "src/scenes/Chat/utils/meetingCallEventContract";
import {
    getMatrixRtcParticipantUserId,
    isCurrentMatrixRtcParticipant
} from "src/scenes/Chat/utils/matrixRtcParticipant";
import { prepareRoomEncryptionForSend } from "src/scenes/Chat/utils/matrixClientManager";
import { getParticipantMediaState } from "src/scenes/Chat/utils/participantMediaState";

import {
    ScreenShareHandover,
    SCREEN_SHARE_PUBLISH_BUDGET_MS,
    SCREEN_SHARE_TAKEN_OVER_MESSAGE
} from "./ScreenShareHandover";
import { createLiveKitScreenShareTransport } from "./LiveKitScreenShareTransport";
import { prepareScreenShareCapture, verifyScreenShareTransmission } from "./screenShareMedia";

const TRANSPORT_ENDPOINT = "/org.matrix.msc4143/rtc/transports";
const MATRIX_RTC_SESSION_EVENT = {
    ENCRYPTION_KEY_CHANGED: "encryption_key_changed",
    MEMBERSHIPS_CHANGED: "memberships_changed"
};
const MATRIX_CALL_NOTIFICATION = "org.matrix.msc4075.call.notify";
const MATRIX_RTC_NOTIFICATION = "org.matrix.msc4075.rtc.notification";
const MATRIX_RTC_NOTIFICATION_PARENT = "org.matrix.msc4075.rtc.notification.parent";
const MATRIX_RTC_ENCRYPTION_KEYS = "io.element.call.encryption_keys";
const MATRIX_RTC_KEYRING_SIZE = 16;
const LEGACY_KEY_DELIVERY_WINDOW_MS = 250;
const MATRIX_ROOM_READY_ATTEMPT_LIMIT = 25;
const MATRIX_ROOM_READY_RETRY_DELAY_MS = 400;
const MEETING_MEMBERSHIP_LEASE_MS = 120000;
const FULL_QUALITY_PARTICIPANT_LIMIT = 9;
const liveKitFocusByClient = new WeakMap();

const wait = duration => new Promise(resolve => window.setTimeout(resolve, duration));

const encodeUnpaddedBase64 = bytes => {
    let binary = "";
    bytes.forEach(byte => {
        binary += String.fromCharCode(byte);
    });
    return window.btoa(binary).replace(/=+$/, "");
};

const decodeBase64Key = value => {
    if (typeof value !== "string" || !/^[A-Za-z0-9+/]+={0,2}$/.test(value)) {
        return null;
    }
    try {
        const paddedValue = value.padEnd(Math.ceil(value.length / 4) * 4, "=");
        const binary = window.atob(paddedValue);
        if (![16, 32].includes(binary.length)) {
            return null;
        }
        return Uint8Array.from(binary, character => character.charCodeAt(0));
    } catch {
        return null;
    }
};

export const waitForMatrixRtcRoom = async (matrixClient, matrixRoomId) => {
    const crypto = matrixClient?.getCrypto?.();
    if (!crypto || !matrixRoomId) {
        throw new Error("Meeting room encryption is not ready.");
    }

    for (let attempt = 0; attempt < MATRIX_ROOM_READY_ATTEMPT_LIMIT; attempt += 1) {
        const matrixRoom = matrixClient.getRoom?.(matrixRoomId);
        const isJoined = matrixRoom?.getMyMembership?.() === "join";
        const encryptionIsReady = isJoined && await crypto.isEncryptionEnabledInRoom(matrixRoomId);

        if (encryptionIsReady) {
            return matrixRoom;
        }

        await wait(MATRIX_ROOM_READY_RETRY_DELAY_MS);
    }

    throw new Error("The encrypted meeting room did not finish syncing. Please try again.");
};

class MatrixRtcKeyProvider extends BaseKeyProvider {
    constructor() {
        super({
            sharedKey: false,
            keyringSize: MATRIX_RTC_KEYRING_SIZE,
            ratchetWindowSize: 0
        });
    }

    async setParticipantKey(key, participantId, keyIndex) {
        const keyBuffer = key.buffer.slice(key.byteOffset, key.byteOffset + key.byteLength);
        const keyMaterial = await createKeyMaterialFromBuffer(keyBuffer);
        this.onSetEncryptionKey(keyMaterial, participantId, keyIndex % MATRIX_RTC_KEYRING_SIZE);
    }
}

const findLiveKitFocus = response => {
    const candidates = [
        ...(response?.rtc_transports || []),
        ...(response?.transports || []),
        ...(response?.["org.matrix.msc4143.rtc_foci"] || [])
    ];

    return candidates.find(candidate =>
        candidate?.type === "livekit" && typeof candidate.livekit_service_url === "string"
    ) || null;
};

const normalizeServiceUrl = serviceUrl => serviceUrl.replace(/\/$/, "");

const normalizeLiveKitUrlForBrowser = serviceUrl => {
    try {
        const liveKitUrl = new URL(serviceUrl);
        const browserHostname = window.location.hostname;

        if (browserHostname && liveKitUrl.hostname.endsWith(".localhost")) {
            liveKitUrl.hostname = browserHostname;
        }

        return liveKitUrl.toString().replace(/\/$/, "");
    } catch {
        return serviceUrl;
    }
};

const getParticipantMediaStream = (previousParticipant, mediaTracks) => {
    const previousStream = previousParticipant?.stream;
    const previousTrackIds = previousStream?.getTracks?.().map(track => track.id) || [];
    const nextTrackIds = mediaTracks.map(track => track.id);
    const tracksAreUnchanged = Boolean(previousStream) &&
        previousTrackIds.length === nextTrackIds.length &&
        previousTrackIds.every(trackId => nextTrackIds.includes(trackId));

    if (tracksAreUnchanged) {
        return previousStream;
    }

    return new MediaStream(mediaTracks);
};

export const discoverLiveKitFocus = async matrixClient => {
    const cachedFocus = liveKitFocusByClient.get(matrixClient);
    if (cachedFocus) {
        return cachedFocus;
    }

    try {
        const response = await matrixClient.http.authedRequest(
            Method.Get,
            TRANSPORT_ENDPOINT,
            undefined,
            undefined,
            { prefix: ClientPrefix.Unstable }
        );
        const focus = findLiveKitFocus(response);
        if (focus) {
            liveKitFocusByClient.set(matrixClient, focus);
            return focus;
        }
    } catch (error) {
        console.warn("Synapse did not advertise a MatrixRTC transport.", error);
    }

    const homeserverUrl = matrixClient.getHomeserverUrl?.();
    if (homeserverUrl) {
        try {
            const response = await fetch(`${normalizeServiceUrl(homeserverUrl)}/.well-known/matrix/client`);
            if (response.ok) {
                const focus = findLiveKitFocus(await response.json());
                if (focus) {
                    liveKitFocusByClient.set(matrixClient, focus);
                    return focus;
                }
            }
        } catch (error) {
            console.warn("Unable to read MatrixRTC client discovery.", error);
        }
    }

    throw new Error("This homeserver has not been configured for MatrixRTC calls.");
};

const exchangeOpenIdForLiveKitToken = async ({ matrixClient, matrixRoomId, serviceUrl }) => {
    const openIdToken = await matrixClient.getOpenIdToken();
    const response = await fetch(`${normalizeServiceUrl(serviceUrl)}/sfu/get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            room: matrixRoomId,
            openid_token: openIdToken,
            device_id: matrixClient.getDeviceId()
        })
    });

    if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`LiveKit authentication failed (${response.status}): ${responseText}`);
    }

    const credentials = await response.json();
    if (!credentials?.url || !credentials?.jwt) {
        throw new Error("LiveKit authentication returned incomplete credentials.");
    }

    return credentials;
};

const getParticipantDisplayName = (matrixRoom, matrixUserId, liveKitParticipant) => {
    const displayName = matrixRoom.getMember?.(matrixUserId)?.name || liveKitParticipant.name || "";
    return getCallParticipantLabel(matrixUserId, displayName);
};

export class MatrixRtcLiveKitSession {
    constructor({ matrixClient, matrixRoom, meetingMetadata, onParticipantsChanged, onSessionChanged, onScreenShareStopped }) {
        this.matrixClient = matrixClient;
        this.matrixRoom = matrixRoom;
        this.meetingMetadata = meetingMetadata || null;
        this.onParticipantsChanged = onParticipantsChanged;
        this.onSessionChanged = onSessionChanged;
        this.onScreenShareStopped = onScreenShareStopped;
        this.screenShareHandover = null;
        this.screenShareTransport = null;
        this.screenShareChanging = false;
        this.screenShareStartTask = null;
        this.screenShareStopTask = null;
        this.screenShareCapture = null;
        this.screenShareLease = null;
        this.screenShareEndedHandler = null;
        this.matrixSession = matrixClient.matrixRTC.getRoomSession(matrixRoom);
        this.liveKitRoom = null;
        this.keyProvider = new MatrixRtcKeyProvider();
        this.e2eeWorker = null;
        this.focus = null;
        this.remoteStreams = new Map();
        this.pendingEncryptionKeys = [];
        this.pendingLegacyEncryptionKeyEvents = new Map();
        this.publishedTracks = new Map();
        this.participantRefreshTimeout = null;
        this.participantRefreshDelay = null;
        this.participantSnapshot = "";
        this.ringEventId = "";
        this.ringCallId = "";
        this.sharedLegacyEncryptionKeys = new Set();
        this.currentLocalEncryptionKey = null;
        this.localEncryptionKeyActivation = null;
        this.legacyKeyReshareTimeout = null;
        this.isClosed = false;

        this.handleEncryptionKeyChanged = this.handleEncryptionKeyChanged.bind(this);
        this.handleLegacyEncryptionKeyEvent = this.handleLegacyEncryptionKeyEvent.bind(this);
        this.handleMembershipsChanged = this.handleMembershipsChanged.bind(this);
        this.matrixSession.on(MATRIX_RTC_SESSION_EVENT.ENCRYPTION_KEY_CHANGED, this.handleEncryptionKeyChanged);
        this.matrixSession.on(MATRIX_RTC_SESSION_EVENT.MEMBERSHIPS_CHANGED, this.handleMembershipsChanged);
        this.matrixClient.on(ClientEvent.Event, this.handleLegacyEncryptionKeyEvent);
        this.matrixClient.on(MatrixEventEvent.Decrypted, this.handleLegacyEncryptionKeyEvent);
    }

    get callId() {
        return this.matrixSession.callId || "";
    }

    get sessionId() {
        return this.callId || this.matrixSession.getOldestMembership?.()?.eventId || "";
    }

    get memberships() {
        return this.matrixSession.memberships;
    }

    get remoteParticipantCount() {
        if (!this.liveKitRoom) {
            return 0;
        }

        let participantCount = 0;
        this.liveKitRoom.remoteParticipants.forEach(participant => {
            if (!isCurrentMatrixRtcParticipant(
                this.memberships,
                participant.identity,
                this.matrixClient.getUserId?.() || ""
            )) {
                participantCount += 1;
            }
        });
        return participantCount;
    }

    async connect({ localStream, ring = false }) {
        try {
            this.focus = this.matrixSession.getFocusInUse() || await discoverLiveKitFocus(this.matrixClient);
            this.matrixSession.joinRoomSession(
                [this.focus],
                undefined,
                {
                    applicationData: this.meetingMetadata
                        ? { [MEETING_EVENT_METADATA_FIELD]: this.meetingMetadata }
                        : undefined,
                    manageMediaKeys: true,
                    membershipEventExpiryMs: this.meetingMetadata
                        ? MEETING_MEMBERSHIP_LEASE_MS
                        : 4 * 60 * 60 * 1000,
                    delayedLeaveEventDelayMs: 8000,
                    delayedLeaveEventRestartMs: 5000
                }
            );

            const ringNotificationResult = ring
                ? this.sendRingNotifications().then(() => null, error => error)
                : Promise.resolve(null);

            const credentials = await exchangeOpenIdForLiveKitToken({
                matrixClient: this.matrixClient,
                matrixRoomId: this.matrixRoom.roomId,
                serviceUrl: this.focus.livekit_service_url
            });

            this.e2eeWorker = new Worker(
                new URL("livekit-client/e2ee-worker", import.meta.url),
                { type: "module" }
            );
            this.liveKitRoom = new Room({
                // Participant video is rendered in a separate call window from raw
                // MediaStreams. LiveKit cannot reliably infer element visibility in
                // that setup, particularly in Firefox, so keep subscriptions active.
                adaptiveStream: false,
                dynacast: true,
                e2ee: {
                    keyProvider: this.keyProvider,
                    worker: this.e2eeWorker
                }
            });
            this.screenShareTransport = createLiveKitScreenShareTransport(this.liveKitRoom);
            this.screenShareHandover = new ScreenShareHandover({
                transport: this.screenShareTransport,
                roomId: this.matrixRoom.roomId,
                clock: () => performance.now(),
                isCurrent: () => this.isScreenShareCallConnected(),
                stopLocal: (trackSid, newOwner, leader) => this.stopScreenShareForHandover(trackSid, newOwner, leader)
            });
            this.screenShareHandover.start();
            this.bindLiveKitEvents();
            this.matrixSession.reemitEncryptionKeys();
            await this.localEncryptionKeyActivation;
            await Promise.all(this.pendingEncryptionKeys.splice(0).map(args => this.applyEncryptionKey(...args)));
            await this.liveKitRoom.connect(
                normalizeLiveKitUrlForBrowser(credentials.url),
                credentials.jwt,
                { autoSubscribe: true }
            );
            await this.liveKitRoom.setE2EEEnabled(true);
            await this.publishStream(localStream);
            const ringNotificationError = await ringNotificationResult;
            if (ringNotificationError) {
                throw ringNotificationError;
            }
            this.publishParticipants();
            this.onSessionChanged?.(this.matrixSession);
            return this;
        } catch (error) {
            await this.disconnect();
            throw error;
        }
    }

    getOwnMembership() {
        const userId = this.matrixClient.getUserId?.();
        const deviceId = this.matrixClient.getDeviceId?.();

        return this.matrixSession.memberships.find(membership =>
            membership.sender === userId && membership.deviceId === deviceId
        );
    }

    async waitForOwnMembershipEventId() {
        const currentEventId = this.getOwnMembership()?.eventId;
        if (currentEventId) {
            return currentEventId;
        }

        return new Promise((resolve, reject) => {
            const timeoutId = window.setTimeout(() => {
                this.matrixSession.off(MATRIX_RTC_SESSION_EVENT.MEMBERSHIPS_CHANGED, handleMembershipsChanged);
                reject(new Error("MatrixRTC membership was not published in time."));
            }, 10000);
            const handleMembershipsChanged = () => {
                const eventId = this.getOwnMembership()?.eventId;
                if (!eventId) {
                    return;
                }
                window.clearTimeout(timeoutId);
                this.matrixSession.off(MATRIX_RTC_SESSION_EVENT.MEMBERSHIPS_CHANGED, handleMembershipsChanged);
                resolve(eventId);
            };

            this.matrixSession.on(MATRIX_RTC_SESSION_EVENT.MEMBERSHIPS_CHANGED, handleMembershipsChanged);
        });
    }

    async sendRingNotifications() {
        this.ringCallId = this.ringCallId || window.crypto?.randomUUID?.() ||
            `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const notificationTimestamp = Date.now();
        const legacyNotificationRequest = this.matrixClient.sendEvent(
            this.matrixRoom.roomId,
            MATRIX_CALL_NOTIFICATION,
            {
                application: "m.call",
                "m.mentions": { user_ids: [], room: true },
                notify_type: "ring",
                call_id: this.ringCallId,
                sender_ts: notificationTimestamp,
                lifetime: 30000,
                [SHARED_CALL_SUMMARY_FIELD]: { hasNotes: false }
            }
        );
        const rtcNotificationResult = this.waitForOwnMembershipEventId()
            .then(membershipEventId => this.matrixClient.sendEvent(
                this.matrixRoom.roomId,
                MATRIX_RTC_NOTIFICATION,
                {
                    "m.mentions": { user_ids: [], room: true },
                    notification_type: "ring",
                    call_id: this.ringCallId,
                    "m.relates_to": {
                        event_id: membershipEventId,
                        rel_type: MATRIX_RTC_NOTIFICATION_PARENT
                    },
                    sender_ts: notificationTimestamp,
                    lifetime: 30000,
                    [SHARED_CALL_SUMMARY_FIELD]: { hasNotes: false }
                }
            ))
            .then(value => ({ value }), error => ({ error }));

        try {
            const legacyNotification = await legacyNotificationRequest;
            this.ringEventId = legacyNotification?.event_id || "";
            rtcNotificationResult.then(result => {
                if (result.error) {
                    console.warn("Unable to send the MatrixRTC notification event.", result.error);
                }
            });
            return;
        } catch (legacyError) {
            const rtcNotification = await rtcNotificationResult;
            if (rtcNotification.error) {
                throw legacyError || rtcNotification.error ||
                    new Error("Unable to notify call participants.");
            }
            this.ringEventId = rtcNotification.value?.event_id || "";
        }
    }

    bindLiveKitEvents() {
        const refresh = () => {
            this.scheduleParticipantsRefresh(40);
            this.reconcileScreenShareOwnership();
        };
        const participantConnected = () => {
            refresh();
            this.replayPendingLegacyEncryptionKeys();
            this.scheduleLegacyKeyReshare();
        };
        this.liveKitRoom.on(RoomEvent.ParticipantConnected, participantConnected);
        [
            RoomEvent.ParticipantDisconnected,
            RoomEvent.TrackPublished,
            RoomEvent.TrackUnpublished,
            RoomEvent.TrackSubscribed,
            RoomEvent.TrackUnsubscribed,
            RoomEvent.TrackMuted,
            RoomEvent.TrackUnmuted,
            RoomEvent.TrackStreamStateChanged
        ].forEach(eventName => this.liveKitRoom.on(eventName, refresh));
        this.liveKitRoom.on(RoomEvent.ActiveSpeakersChanged, () => {
            this.scheduleParticipantsRefresh(400);
        });
        this.liveKitRoom.on(RoomEvent.Disconnected, refresh);
        this.liveKitRoom.on(RoomEvent.EncryptionError, error => {
            console.error("Unable to decrypt MatrixRTC media.", error);
        });
    }

    async handleEncryptionKeyChanged(key, keyIndex, membership, rtcBackendIdentity) {
        const participantId = rtcBackendIdentity || `${membership?.userId || ""}:${membership?.deviceId || ""}`;
        if (!participantId || participantId === ":") {
            return;
        }
        const normalizedKeyIndex = keyIndex % MATRIX_RTC_KEYRING_SIZE;
        const isLocalKey = membership?.userId === this.matrixClient.getUserId?.() &&
            membership?.deviceId === this.matrixClient.getDeviceId?.();

        if (isLocalKey) {
            this.currentLocalEncryptionKey = { key, keyIndex: normalizedKeyIndex };
            const activation = this.activateLocalEncryptionKey(
                key,
                normalizedKeyIndex,
                participantId
            );
            this.localEncryptionKeyActivation = activation;
            await activation;
            return;
        }

        await this.installEncryptionKey(key, normalizedKeyIndex, participantId);
    }

    async activateLocalEncryptionKey(key, keyIndex, participantId) {
        await this.shareEncryptionKeyWithLegacyClients(key, keyIndex);
        await wait(LEGACY_KEY_DELIVERY_WINDOW_MS);
        await this.installEncryptionKey(key, keyIndex, participantId);
    }

    async installEncryptionKey(key, keyIndex, participantId) {
        if (!this.liveKitRoom) {
            this.pendingEncryptionKeys.push([key, keyIndex, participantId]);
        } else {
            await this.applyEncryptionKey(key, keyIndex, participantId);
        }
    }

    async applyEncryptionKey(key, keyIndex, participantId) {
        await this.keyProvider.setParticipantKey(key, participantId, keyIndex);
    }

    async shareEncryptionKeyWithLegacyClients(key, keyIndex, force = false) {
        const normalizedKeyIndex = keyIndex % MATRIX_RTC_KEYRING_SIZE;
        const encodedKey = encodeUnpaddedBase64(key);
        const deduplicationKey = `${normalizedKeyIndex}:${encodedKey}`;
        if (!force && this.sharedLegacyEncryptionKeys.has(deduplicationKey)) {
            return;
        }
        this.sharedLegacyEncryptionKeys.add(deduplicationKey);

        try {
            await prepareRoomEncryptionForSend(
                this.matrixClient,
                this.matrixRoom,
                this.memberships.map(membership => membership.userId || membership.sender).filter(Boolean)
            );
            await this.matrixClient.sendEvent(this.matrixRoom.roomId, MATRIX_RTC_ENCRYPTION_KEYS, {
                call_id: "",
                device_id: this.matrixClient.getDeviceId(),
                sent_ts: Date.now(),
                keys: [{ index: normalizedKeyIndex, key: encodedKey }]
            });
        } catch (error) {
            this.sharedLegacyEncryptionKeys.delete(deduplicationKey);
            console.warn("Unable to share the MatrixRTC media key with a legacy client.", error);
        }
    }

    handleLegacyEncryptionKeyEvent(event, error) {
        if (
            error ||
            event?.getRoomId?.() !== this.matrixRoom.roomId ||
            event?.getType?.() !== MATRIX_RTC_ENCRYPTION_KEYS
        ) {
            return;
        }

        const sender = event.getSender?.();
        const content = event.getContent?.() || {};
        if (!sender || content.call_id !== "" || typeof content.device_id !== "string") {
            return;
        }
        if (
            sender === this.matrixClient.getUserId?.() &&
            content.device_id === this.matrixClient.getDeviceId?.()
        ) {
            return;
        }

        const legacyParticipantIdentity = `${sender}:${content.device_id}`;
        const membership = this.memberships.find(item =>
            item.userId === sender && item.deviceId === content.device_id
        ) || (this.liveKitRoom?.remoteParticipants?.has?.(legacyParticipantIdentity) ? {
            userId: sender,
            deviceId: content.device_id,
            rtcBackendIdentity: legacyParticipantIdentity,
        } : null);
        if (!membership) {
            this.pendingLegacyEncryptionKeyEvents.set(legacyParticipantIdentity, event);
            return;
        }
        this.pendingLegacyEncryptionKeyEvents.delete(legacyParticipantIdentity);

        for (const item of Array.isArray(content.keys) ? content.keys : []) {
            const keyIndex = Number(item?.index);
            const key = decodeBase64Key(item?.key);
            if (!Number.isInteger(keyIndex) || keyIndex < 0 || keyIndex > 255 || !key) {
                continue;
            }
            this.handleEncryptionKeyChanged(
                key,
                keyIndex,
                membership,
                membership.rtcBackendIdentity
            ).catch(keyError => {
                console.warn("Unable to install a legacy MatrixRTC media key.", keyError);
            });
        }
    }

    handleMembershipsChanged() {
        this.replayPendingLegacyEncryptionKeys();
        this.scheduleLegacyKeyReshare();
        this.scheduleParticipantsRefresh(40);
        this.onSessionChanged?.(this.matrixSession);
    }

    replayPendingLegacyEncryptionKeys() {
        const pendingEvents = [...this.pendingLegacyEncryptionKeyEvents.values()];
        pendingEvents.forEach(event => this.handleLegacyEncryptionKeyEvent(event));
    }

    scheduleLegacyKeyReshare() {
        window.clearTimeout(this.legacyKeyReshareTimeout);
        this.legacyKeyReshareTimeout = window.setTimeout(() => {
            this.legacyKeyReshareTimeout = null;
            const currentKey = this.currentLocalEncryptionKey;
            if (!currentKey || this.isClosed) {
                return;
            }
            this.shareEncryptionKeyWithLegacyClients(currentKey.key, currentKey.keyIndex, true)
                .catch(error => console.warn("Unable to re-share the current MatrixRTC media key.", error));
        }, LEGACY_KEY_DELIVERY_WINDOW_MS);
    }

    scheduleParticipantsRefresh(delay) {
        if (this.isClosed) {
            return;
        }
        if (this.participantRefreshTimeout && this.participantRefreshDelay <= delay) {
            return;
        }
        window.clearTimeout(this.participantRefreshTimeout);
        this.participantRefreshDelay = delay;
        this.participantRefreshTimeout = window.setTimeout(() => {
            this.participantRefreshTimeout = null;
            this.participantRefreshDelay = null;
            this.publishParticipants();
        }, delay);
    }

    publishParticipants() {
        if (!this.liveKitRoom) {
            return;
        }

        const activeSpeakerIds = new Set(this.liveKitRoom.activeSpeakers.map(item => item.identity));
        const remoteParticipantCount = this.liveKitRoom.remoteParticipants.size;
        const streams = [];
        this.liveKitRoom.remoteParticipants.forEach(participant => {
            if (isCurrentMatrixRtcParticipant(
                this.memberships,
                participant.identity,
                this.matrixClient.getUserId?.() || ""
            )) {
                return;
            }

            const peerUserId = getMatrixRtcParticipantUserId(this.memberships, participant.identity);
            const displayName = getParticipantDisplayName(this.matrixRoom, peerUserId, participant);
            const mediaTracks = [];
            participant.trackPublications.forEach(publication => {
                const mediaTrack = publication.track?.mediaStreamTrack;
                if (mediaTrack && publication.source !== Track.Source.ScreenShare) {
                    mediaTracks.push(mediaTrack);
                }
            });

            const previousParticipant = this.remoteStreams.get(participant.identity);
            const participantStream = getParticipantMediaStream(previousParticipant, mediaTracks);

            const participantMediaState = getParticipantMediaState(participant);
            const cameraPublication = participantMediaState.cameraPublication;
            const cameraTrack = cameraPublication?.track;
            if (cameraPublication?.setVideoQuality) {
                const keepHighQuality = remoteParticipantCount <= FULL_QUALITY_PARTICIPANT_LIMIT
                    || activeSpeakerIds.has(participant.identity);
                cameraPublication.setVideoQuality(
                    keepHighQuality ? VideoQuality.HIGH : VideoQuality.LOW
                );
            }

            streams.push({
                callId: participant.identity,
                peerUserId,
                displayName,
                stream: participantStream,
                videoTrack: cameraTrack || null,
                isSpeaking: activeSpeakerIds.has(participant.identity),
                isMuted: !participantMediaState.microphoneEnabled,
                cameraOff: !participantMediaState.cameraEnabled
            });

            const screenPublication = participant.getTrackPublication(Track.Source.ScreenShare);
            const screenVideoTrack = screenPublication?.track;
            const screenTrack = screenVideoTrack?.mediaStreamTrack;
            if (screenTrack) {
                const screenCallId = `${participant.identity}:screen-share`;
                const previousScreenShare = this.remoteStreams.get(screenCallId);
                const screenStream = getParticipantMediaStream(previousScreenShare, [screenTrack]);
                streams.push({
                    callId: screenCallId,
                    peerUserId,
                    displayName,
                    stream: screenStream,
                    videoTrack: screenVideoTrack,
                    isScreenShare: true
                });
            }
        });

        const participantSnapshot = JSON.stringify(streams.map(item => ({
            callId: item.callId,
            peerUserId: item.peerUserId,
            displayName: item.displayName,
            trackIds: item.stream?.getTracks?.().map(track => track.id).sort() || [],
            isSpeaking: Boolean(item.isSpeaking),
            isMuted: Boolean(item.isMuted),
            cameraOff: Boolean(item.cameraOff),
            isScreenShare: Boolean(item.isScreenShare)
        })));

        this.remoteStreams = new Map(streams.map(item => [item.callId, item]));
        if (participantSnapshot === this.participantSnapshot) {
            return;
        }
        this.participantSnapshot = participantSnapshot;
        this.onParticipantsChanged?.(streams);
    }

    async publishStream(stream) {
        for (const track of stream?.getTracks?.() || []) {
            await this.publishTrack(track);
        }
    }

    async publishTrack(track, source = null) {
        if (!this.liveKitRoom || !track) {
            return;
        }
        const kind = track.kind;
        const trackSource = source || (kind === "audio" ? Track.Source.Microphone : Track.Source.Camera);
        const existing = this.publishedTracks.get(trackSource);
        if (existing?.replaceTrack) {
            await existing.replaceTrack(track, {
                stopProcessor: true,
                userProvidedTrack: true
            });
            return;
        }
        if (existing) {
            await this.liveKitRoom.localParticipant.unpublishTrack(existing, false);
        }
        const publication = await this.liveKitRoom.localParticipant.publishTrack(track, {
            source: trackSource,
            stopMicTrackOnMute: false
        });
        this.publishedTracks.set(trackSource, publication.track);
    }

    async replaceTrack(track, source = null) {
        await this.publishTrack(track, source);
    }

    async setMicrophoneEnabled(enabled) {
        await this.liveKitRoom?.localParticipant.setMicrophoneEnabled(enabled);
    }

    isScreenShareCallConnected() {
        return !this.isClosed && this.liveKitRoom?.state === ConnectionState.Connected;
    }

    async startScreenShare(track) {
        if (!track || !this.isScreenShareCallConnected() || !this.screenShareHandover) {
            track?.stop();
            throw new Error("The MatrixRTC call is unavailable.");
        }
        if (this.screenShareChanging || this.screenShareCapture) {
            track.stop();
            throw new RpcError(2001, "Screen sharing is already changing");
        }
        this.screenShareChanging = true;
        this.screenShareCapture = track;
        this.screenShareStartTask = this.publishPreparedScreenShare(track);
        try {
            await this.screenShareStartTask;
        } finally {
            this.screenShareStartTask = null;
            this.screenShareChanging = false;
            this.reconcileScreenShareOwnership();
        }
    }

    async publishPreparedScreenShare(track) {
        const room = this.liveKitRoom;
        const transfers = this.screenShareHandover;
        let lease = null;
        let localTrack = null;
        let published = false;
        const isCurrent = () => this.isScreenShareCallConnected() && this.liveKitRoom === room &&
            this.screenShareCapture === track && track.readyState === "live";
        try {
            track.contentHint = "detail";
            await prepareScreenShareCapture(track, isCurrent);
            if (!isCurrent()) {
                throw new Error("Screen sharing was cancelled");
            }
            lease = await transfers.acquire(window.crypto.randomUUID());
            if (!isCurrent()) {
                throw new Error("Screen sharing was cancelled");
            }
            transfers.verify(lease, SCREEN_SHARE_PUBLISH_BUDGET_MS);
            localTrack = new LocalVideoTrack(track, undefined, true);
            const publication = await room.localParticipant.publishTrack(localTrack, {
                source: Track.Source.ScreenShare
            });
            this.publishedTracks.set(Track.Source.ScreenShare, publication.track);
            await verifyScreenShareTransmission(localTrack, isCurrent);
            if (!isCurrent()) {
                throw new Error("Screen sharing was cancelled");
            }
            transfers.verifyPublished(lease);
            this.screenShareEndedHandler = () => {
                this.stopScreenShare().catch(error => console.warn("Unable to stop ended screen capture.", error));
            };
            track.addEventListener("ended", this.screenShareEndedHandler);
            this.screenShareLease = lease;
            published = true;
        } finally {
            if (!published) {
                try {
                    // Await publication completion before cleanup, so late SDK completion cannot resurrect a share.
                    if (localTrack) {
                        await room.localParticipant.unpublishTrack(localTrack, true);
                    }
                } finally {
                    track.stop();
                    if (this.screenShareCapture === track) {
                        this.screenShareCapture = null;
                        this.publishedTracks.delete(Track.Source.ScreenShare);
                    }
                    if (lease) {
                        transfers.cancel(lease).catch(error => console.warn("Unable to cancel screen share grant.", error));
                    }
                }
            }
        }
    }

    async stopScreenShareForHandover(trackSid, newOwner, leader) {
        if (this.screenShareChanging) {
            throw new RpcError(2001, "Screen sharing is changing");
        }
        if (!this.isScreenShareCallConnected() || this.screenShareHandover.leaderIdentity() !== leader ||
            !this.screenShareTransport.joinedIdentities().includes(newOwner)) {
            throw new RpcError(2002, "Share membership changed");
        }
        const publication = this.liveKitRoom.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        if (publication?.trackSid !== trackSid) {
            return;
        }
        // This stop can be running inside the leader's claim mutex. Cancelling
        // the old lease must not wait for that same mutex before acknowledging.
        await this.stopEstablishedScreenShare(SCREEN_SHARE_TAKEN_OVER_MESSAGE, false);
    }

    reconcileScreenShareOwnership() {
        if (!this.isScreenShareCallConnected() || this.screenShareChanging || !this.screenShareCapture) {
            return;
        }
        const identities = this.screenShareTransport.publications().map(item => item.deviceIdentity);
        identities.sort();
        if (identities.length > 1 && identities[0] !== this.liveKitRoom.localParticipant.identity) {
            this.stopScreenShare(SCREEN_SHARE_TAKEN_OVER_MESSAGE).catch(error => {
                console.warn("Unable to resolve overlapping screen shares.", error);
            });
        }
    }

    async stopScreenShare(reason) {
        if (this.screenShareStartTask) {
            this.screenShareCapture?.stop();
            await this.screenShareStartTask.catch(() => undefined);
            return;
        }
        await this.stopEstablishedScreenShare(reason, true);
    }

    async stopEstablishedScreenShare(reason, waitForReservationRelease) {
        if (this.screenShareStopTask) {
            return this.screenShareStopTask;
        }
        this.screenShareChanging = true;
        this.screenShareStopTask = this.unpublishScreenShare(reason, waitForReservationRelease);
        try {
            await this.screenShareStopTask;
        } finally {
            this.screenShareStopTask = null;
            this.screenShareChanging = false;
        }
    }

    async unpublishScreenShare(reason, waitForReservationRelease) {
        const track = this.screenShareCapture;
        const lease = this.screenShareLease;
        const transfers = this.screenShareHandover;
        const publication = this.liveKitRoom?.localParticipant.getTrackPublication(Track.Source.ScreenShare);
        let unpublished = false;
        try {
            if (publication?.track) {
                await this.liveKitRoom.localParticipant.unpublishTrack(publication.track, true);
            }
            unpublished = true;
        } finally {
            if (track && this.screenShareEndedHandler) {
                track.removeEventListener("ended", this.screenShareEndedHandler);
            }
            track?.stop();
            this.screenShareCapture = null;
            this.screenShareLease = null;
            this.screenShareEndedHandler = null;
            this.publishedTracks.delete(Track.Source.ScreenShare);
            if (lease && unpublished) {
                const cancellation = transfers.cancel(lease).catch(error => {
                    console.warn("Unable to release stopped screen share grant.", error);
                });
                if (waitForReservationRelease) {
                    await cancellation;
                }
            }
            if (track || publication) {
                this.onScreenShareStopped?.(reason);
            }
        }
    }

    async disconnect() {
        if (this.isClosed) {
            return;
        }
        this.isClosed = true;
        this.screenShareHandover?.close();
        await this.stopScreenShare().catch(error => console.warn("Unable to clean up screen sharing.", error));
        this.matrixSession.off(MATRIX_RTC_SESSION_EVENT.ENCRYPTION_KEY_CHANGED, this.handleEncryptionKeyChanged);
        this.matrixSession.off(MATRIX_RTC_SESSION_EVENT.MEMBERSHIPS_CHANGED, this.handleMembershipsChanged);
        this.matrixClient.off(ClientEvent.Event, this.handleLegacyEncryptionKeyEvent);
        this.matrixClient.off(MatrixEventEvent.Decrypted, this.handleLegacyEncryptionKeyEvent);
        this.liveKitRoom?.disconnect(true);
        this.e2eeWorker?.terminate();
        window.clearTimeout(this.participantRefreshTimeout);
        window.clearTimeout(this.legacyKeyReshareTimeout);
        this.participantRefreshTimeout = null;
        this.participantRefreshDelay = null;
        await this.matrixSession.leaveRoomSession(5000);
        this.remoteStreams.clear();
        this.pendingLegacyEncryptionKeyEvents.clear();
        this.onParticipantsChanged?.([]);
    }
}
